"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentTranslationEntrySchema = exports.PublishedContentTranslationStatusSchema = exports.ContentTranslationStatusSchema = exports.ContentI18nRowStateSchema = exports.CONTENT_I18N_ROW_STATE_KEY = exports.DEFAULT_CONTENT_I18N_POLICY = exports.ContentI18nPolicySchema = exports.ContentI18nMissingTranslationPolicySchema = void 0;
exports.parseContentI18nRowState = parseContentI18nRowState;
exports.parseContentTranslationRows = parseContentTranslationRows;
exports.isPublishedContentTranslation = isPublishedContentTranslation;
exports.isContentI18nTranslatableColumn = isContentI18nTranslatableColumn;
exports.isContentI18nTranslatableSourceColumn = isContentI18nTranslatableSourceColumn;
const zod_1 = require("zod");
exports.ContentI18nMissingTranslationPolicySchema = zod_1.z.enum([
    'default-locale-fallback',
    'strict',
]);
exports.ContentI18nPolicySchema = zod_1.z.object({
    missingTranslation: exports.ContentI18nMissingTranslationPolicySchema,
});
exports.DEFAULT_CONTENT_I18N_POLICY = {
    missingTranslation: 'default-locale-fallback',
};
exports.CONTENT_I18N_ROW_STATE_KEY = '__contentI18n';
exports.ContentI18nRowStateSchema = zod_1.z.object({
    state: zod_1.z.enum(['translated', 'fallback']),
    requestedLocale: zod_1.z.string().min(2),
    resolvedLocale: zod_1.z.string().min(2),
    defaultLocale: zod_1.z.string().min(2),
    missingFields: zod_1.z.array(zod_1.z.string()),
});
function parseContentI18nRowState(value) {
    const parsed = exports.ContentI18nRowStateSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
exports.ContentTranslationStatusSchema = zod_1.z.enum([
    'draft',
    'pending',
    'approved',
    'published',
    'rejected',
]);
exports.PublishedContentTranslationStatusSchema = zod_1.z.enum([
    'approved',
    'published',
]);
exports.ContentTranslationEntrySchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    entityDb: zod_1.z.string().min(1),
    entityRowId: zod_1.z.string().min(1),
    field: zod_1.z.string().min(1),
    locale: zod_1.z.string().min(2),
    value: zod_1.z.string(),
    status: exports.ContentTranslationStatusSchema,
    sourceHash: zod_1.z.string().min(1),
    updatedAt: zod_1.z.string().min(1),
});
const REQUIRED_FIELDS = [
    'entityDb',
    'entityRowId',
    'field',
    'locale',
    'value',
    'status',
    'sourceHash',
    'updatedAt',
];
function parseContentTranslationRows(rows, columns) {
    const columnIdByName = new Map(columns.map(column => [column.name, column.id]));
    return rows.map(row => {
        const raw = { id: row.id };
        for (const field of REQUIRED_FIELDS) {
            raw[field] = readTranslationCell(row.cells, columnIdByName, field);
        }
        return exports.ContentTranslationEntrySchema.parse(raw);
    });
}
function isPublishedContentTranslation(entry) {
    return exports.PublishedContentTranslationStatusSchema.safeParse(entry.status)
        .success;
}
function isContentI18nTranslatableColumn(column) {
    const name = column.name.trim().toLowerCase();
    return (['title', 'text', 'rich-text'].includes(column.type) &&
        !name.startsWith('_') &&
        !CONTENT_I18N_NON_TRANSLATABLE_FIELD_NAMES.has(name) &&
        !name.endsWith('id') &&
        !name.endsWith('ids'));
}
/** Resolve the single editable translation surface for a concrete source. */
function isContentI18nTranslatableSourceColumn(input) {
    if (!isContentI18nTranslatableColumn(input.column))
        return false;
    // Knowledge-base `body` is rebuilt from canonical structured `blocks`.
    // Translating both creates two authorities for the same article content.
    if (input.contentModel === 'knowledge-base-entry' &&
        input.column.name.trim().toLowerCase() === 'body' &&
        input.columns.some(column => column.name.trim().toLowerCase() === 'blocks')) {
        return false;
    }
    return true;
}
const CONTENT_I18N_NON_TRANSLATABLE_FIELD_NAMES = new Set([
    'status',
    'visibility',
    'audience',
    'published',
    'publishedat',
    'scheduledat',
    'updatedat',
    'createdat',
    'slug',
    'path',
    'canonical',
    'hreflanggroup',
    'locale',
    'ctaurl',
    'email',
]);
function readTranslationCell(cells, columnIdByName, field) {
    const columnId = columnIdByName.get(field);
    return columnId ? cells[columnId] : cells[field];
}
